import { Link, createFileRoute } from '@tanstack/react-router';
import {
  BookOpen,
  CalendarClock,
  Compass,
  LineChart,
  Plus,
  Target,
  UtensilsCrossed,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { EMPTY_ACTION_CLASS, EMPTY_SECONDARY_CLASS } from '@/components/emptyStateClasses';
import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { DayNav } from '@/components/food/DayNav';
import { EditEntrySheet } from '@/components/food/EditEntrySheet';
import { FoodSyncBadge } from '@/components/food/FoodSyncBadge';
import { CalorieRing, MacroBars } from '@/components/food/MacroBars';
import { MealSection } from '@/components/food/MealSection';
import { formatKcal, todayIso } from '@/components/food/format';
import { explainError } from '@/lib/errors';
import { FOOD_DIARY_COPY, PLANNED_COPY } from '@/lib/i18n/nutrition';
import { TOUR_UI } from '@/lib/i18n/tours';
import { useTour } from '@/lib/tour/context';
import { markMealEntriesEaten, pruneSyncedMealEntries } from '@/lib/offline/meals';
import type { DayEntryView, DayView } from '@/lib/offline/mealDay';
import { useDayLog } from '@/lib/queries/meals';
import { foodDateSchema, type FoodDateSearch } from '@/components/food/searchParams';

/**
 * Diario del día.
 *
 * El día vive en la URL (`/food?date=2026-03-12`), así que se puede compartir,
 * volver atrás desde una ficha y recargar sin perderlo. Lo que se pinta no es
 * la respuesta del servidor sino su fusión con lo que hay en el dispositivo:
 * sin conexión el diario sigue funcionando entero.
 */
export const Route = createFileRoute('/_app/food/')({
  validateSearch: (search: Record<string, unknown>): FoodDateSearch => foodDateSchema.parse(search),
  component: FoodPage,
});

function FoodPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const date = search.date ?? todayIso();
  const { day, isPending, isError, error, refetch } = useDayLog(date);
  const [editing, setEditing] = useState<DayEntryView | null>(null);
  const tour = useTour();

  useEffect(() => {
    if (search.meal && day)
      document.getElementById(`meal-${search.meal}`)?.scrollIntoView({ block: 'start' });
  }, [search.meal, day]);

  // El diario de hace dos meses lo sirve el servidor; aquí sólo estorba.
  useEffect(() => {
    void pruneSyncedMealEntries();
  }, []);

  return (
    <>
      <PageHeader
        title="Comida"
        subtitle="Apunta lo que comes, con o sin cobertura."
        action={
          <Link
            to="/food/add"
            search={{ date }}
            data-tour="food-add"
            data-tour-avoid
            className="fixed right-[max(1rem,env(safe-area-inset-right,0px))] bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px))] z-30 flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-brand px-4 text-sm font-semibold text-brand-ink shadow-lg transition-colors hover:bg-brand-strong active:bg-brand-strong md:static md:min-h-11 md:rounded-xl md:shadow-none"
          >
            <Plus className="size-5" aria-hidden="true" />
            {FOOD_DIARY_COPY.add}
          </Link>
        }
      />

      {/* Pantalla ancha: el día y su resumen en una columna fija a la izquierda; las
          comidas, a la derecha. En móvil las dos columnas se apilan sin más. */}
      <div className="flex flex-col gap-4 pb-16 md:pb-0 xl:grid xl:grid-cols-[22rem_minmax(0,1fr)] xl:items-start xl:gap-6 2xl:grid-cols-[24rem_minmax(0,1fr)]">
        <div className="flex flex-col gap-4 xl:sticky xl:top-6">
          <DayNav
            date={date}
            onChange={(next) => void navigate({ search: { date: next }, replace: true })}
          />

          <FoodSyncBadge />

          {isError ? (
            <ErrorBanner
              title="No hemos podido cargar el diario del servidor"
              message={diaryLoadMessage(error)}
              onRetry={refetch}
            />
          ) : null}

          <Card data-tour="food-summary">
            {isPending && day.meals.every((meal) => meal.entries.length === 0) ? (
              <div className="flex justify-center py-6">
                <Spinner size="lg" />
              </div>
            ) : (
              <>
                <CalorieRing
                  consumed={day.totals.kcal}
                  target={day.targets?.kcal ?? null}
                  remaining={day.remaining.kcal}
                />
                <MacroBars totals={day.totals} targets={day.targets} className="mt-4" />
              </>
            )}
          </Card>

          <PlannedNote day={day} />

          {day.targets === null ? <TargetsPrompt /> : null}

          <Shortcuts />
        </div>

        <div className="flex flex-col gap-4">
          {!isPending && day.meals.every((meal) => meal.entries.length === 0) ? (
            <EmptyState
              icon={UtensilsCrossed}
              title={date === todayIso() ? FOOD_DIARY_COPY.emptyToday : FOOD_DIARY_COPY.emptyDay}
              description={FOOD_DIARY_COPY.emptyHint}
              action={
                <>
                  <Link to="/food/add" search={{ date }} className={EMPTY_ACTION_CLASS}>
                    <Plus className="size-4" aria-hidden="true" />
                    {FOOD_DIARY_COPY.add}
                  </Link>
                  <button
                    type="button"
                    className={EMPTY_SECONDARY_CLASS}
                    onClick={() => tour.start('comida')}
                  >
                    <Compass className="size-4" aria-hidden="true" />
                    {TOUR_UI.emptyLaunch}
                  </button>
                </>
              }
            />
          ) : null}
          {day.meals.map((meal) => (
            <MealSection key={meal.meal} meal={meal} onEdit={setEditing} />
          ))}
        </div>
      </div>

      {editing !== null ? (
        <EditEntrySheet key={editing.clientId} entry={editing} onClose={() => setEditing(null)} />
      ) : null}
    </>
  );
}

/**
 * Sin red, el diario sigue funcionando con lo del dispositivo: lo que se
 * apunte va a la cola y se sube solo. Con otro fallo (el servidor), también
 * se guarda en local, y conviene decirlo.
 */
function diaryLoadMessage(error: unknown): string {
  const explained = explainError(error, { queued: true });
  return explained.kind === 'offline'
    ? explained.message
    : `${explained.message} Lo que apuntes se guarda igualmente en el dispositivo.`;
}

/**
 * Aviso de lo planificado que queda por marcar en el día. Los totales de
 * arriba son sólo lo comido, así que sin él un día planificado parecería
 * vacío.
 */
function PlannedNote({ day }: { day: DayView }) {
  const [marking, setMarking] = useState(false);
  const planned = day.meals.flatMap((meal) =>
    meal.entries.filter((entry) => entry.status === 'planned'),
  );
  if (planned.length === 0) return null;

  async function markAll(): Promise<void> {
    setMarking(true);
    try {
      await markMealEntriesEaten(planned);
    } finally {
      setMarking(false);
    }
  }

  return (
    <div
      data-tour="food-planned"
      className="flex flex-col gap-3 rounded-2xl border border-dashed border-line p-4"
    >
      <div className="flex items-start gap-3">
        <CalendarClock className="size-5 shrink-0 text-ink-muted" aria-hidden="true" />
        <p className="min-w-0 flex-1 text-sm text-ink-muted">
          <span className="block font-semibold text-ink">
            {formatKcal(day.plannedTotals.kcal)} planificadas
          </span>
          {PLANNED_COPY.hint}
        </p>
      </div>
      {planned.length > 1 ? (
        <Button variant="secondary" loading={marking} onClick={() => void markAll()}>
          {PLANNED_COPY.markDay}
        </Button>
      ) : null}
    </div>
  );
}

/** Tarjeta que aparece mientras el usuario no ha fijado objetivos. */
function TargetsPrompt() {
  return (
    <Link
      to="/food/targets"
      className="flex items-center gap-3 rounded-2xl border border-dashed border-brand/40 bg-brand/5 p-4 transition-colors hover:bg-brand/10 active:bg-brand/10"
    >
      <Target className="size-6 shrink-0 text-brand" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-ink">Define tus objetivos</span>
        <span className="mt-0.5 block text-sm text-ink-muted">
          Sin ellos el diario cuenta calorías, pero no sabe si vas bien.
        </span>
      </span>
    </Link>
  );
}

const SHORTCUTS = [
  { to: '/food/recipes', label: 'Recetas', icon: BookOpen },
  { to: '/food/targets', label: 'Objetivos', icon: Target },
  { to: '/food/trends', label: 'Tendencias', icon: LineChart },
] as const;

/** Fila de accesos al resto del bloque de nutrición. */
function Shortcuts() {
  return (
    <nav aria-label="Más de nutrición" className="grid grid-cols-3 gap-2">
      {SHORTCUTS.map(({ to, label, icon: Icon }) => (
        <Link
          key={to}
          to={to}
          className="flex min-h-16 flex-col items-center justify-center gap-1 rounded-2xl border border-line bg-surface-raised text-xs font-medium text-ink-muted transition-colors hover:bg-surface-overlay active:bg-surface-overlay"
        >
          <Icon className="size-5 text-brand" aria-hidden="true" />
          {label}
        </Link>
      ))}
    </nav>
  );
}
